import { create } from 'zustand'
import { getAuthToken } from '../utils/tokenUtils'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001'

const MAX_RETRY_COUNT = 10 // Maximum number of retry attempts
const INITIAL_RETRY_DELAY = 2000 // Start with 2 seconds
const MAX_RETRY_DELAY = 30000 // Maximum delay of 30 seconds

const useMaskStore = create((set, get) => ({
  masks: [],
  isLoading: false,
  error: null,
  retryCount: 0,
  retryDelay: INITIAL_RETRY_DELAY,
  nextRetryTime: null,
  activeMaskId: null,
  navigationPath: [],

  setMasks: (masks) => set({ masks }),
  
  setActiveMask: (maskId) => set({ activeMaskId: maskId }),
  
  setNavigationPath: (path) => set({ navigationPath: path }),
  
  resetMaskState: () => set({
    masks: [],
    isLoading: false,
    error: null,
    retryCount: 0,
    retryDelay: INITIAL_RETRY_DELAY,
    nextRetryTime: null,
    activeMaskId: null,
    navigationPath: []
  }),

  updateMaskStatus: (maskId, updates) => set((state) => ({
    masks: state.masks.map(mask => 
      mask._id === maskId 
        ? { 
            ...mask, 
            ...updates,
            lastUpdated: new Date().toISOString()
          }
        : mask
    )
  })),

  addMask: (mask) => set((state) => ({
    masks: [...state.masks, mask]
  })),

  setLoading: (isLoading) => set({ isLoading }),
  
  setError: (error) => set((state) => ({ 
    error,
    ...(error ? {
      nextRetryTime: Date.now() + state.retryDelay
    } : {
      retryCount: 0,
      retryDelay: INITIAL_RETRY_DELAY,
      nextRetryTime: null
    })
  })),

  getMaskById: (maskId) => {
    const state = get()
    return state.masks.find(mask => mask._id === maskId)
  },

  startPolling: (maskId) => {
    const pollInterval = setInterval(async () => {
      const state = get()
      
      try {
        const token = getAuthToken()
        const response = await fetch(`${API_BASE_URL}/masks/${maskId}/status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (!response.ok) {
          throw new Error('Failed to check mask status')
        }

        const data = await response.json()
        const currentMask = get().getMaskById(maskId)
        
        // Reset retry state on successful request
        if (state.retryCount > 0) {
          set({
            retryCount: 0,
            retryDelay: INITIAL_RETRY_DELAY,
            nextRetryTime: null,
            error: null
          })
        }
        
        if (!currentMask || 
            currentMask.status !== data.status || 
            currentMask.estimatedWait !== data.estimatedWait) {
          get().updateMaskStatus(maskId, {
            status: data.status,
            error: data.error,
            estimatedWait: data.estimatedWait
          })
        }
        
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(pollInterval)
        }
      } catch (error) {
        console.error('Polling error:', error)
        
        const newRetryCount = state.retryCount + 1
        const newRetryDelay = Math.min(
          state.retryDelay * 1.5, // Exponential backoff
          MAX_RETRY_DELAY
        )
        
        if (newRetryCount <= MAX_RETRY_COUNT) {
          set({
            error: 'Failed to check mask status',
            retryCount: newRetryCount,
            retryDelay: newRetryDelay,
            nextRetryTime: Date.now() + newRetryDelay
          })
          
          // Don't clear the interval, let it retry
        } else {
          set({
            error: 'Maximum retry attempts reached. Please try again later.',
            retryCount: 0,
            retryDelay: INITIAL_RETRY_DELAY,
            nextRetryTime: null
          })
          clearInterval(pollInterval)
        }
      }
    }, 2000)

    return pollInterval
  },

  clearError: () => set({ 
    error: null,
    retryCount: 0,
    retryDelay: INITIAL_RETRY_DELAY,
    nextRetryTime: null
  })
}))

export default useMaskStore 